import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  adminNavApi,
  ADMIN_NAV_KEYS,
  type RebalanceRunRow,
  type RebalanceScope,
  type RebalanceStartResult,
} from '@/features/navigator/adminNavigation.api';
import { navError } from '@/features/navigator/navigator.api';
import { toast } from '@/stores/toast.store';
import { useLang, useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * E-36 · «Repartir parejo» (Edd, 2026-10-21, propuesta B).
 *
 * Es la puerta de la pantalla al emparejamiento a mano que el server ya tiene
 * desde E-35b (POST admin/navigation/rebalance + GET rebalance/runs). No hay
 * lógica de reparto acá: el server decide qué toma (buzón + cuentas de baja)
 * y nunca quita casos trabajados. Esta tarjeta solo:
 *  - pide confirmación en el modal del chasis (nada arranca sin confirmar);
 *  - dice la verdad de la respuesta (`switch_off`, `already_running`);
 *  - muestra la última corrida de cada tipo, sacada de `rebalance/runs`;
 *  - mientras una corrida esté `running`, refresca sola y apaga su botón.
 *
 * Solo la ve el admin: el POST es `@Roles('admin')`. Un supervisor que la
 * viera tendría un botón que siempre rebota con 403.
 */

const SCOPES: RebalanceScope[] = ['full', 'closer'];

/** Cada cuánto se re-mira la lista mientras hay una corrida viva. */
export const REBALANCE_POLL_MS = 4000;

const NY = 'America/New_York';

/** La última corrida de cada tipo. La lista viene ordenada, más nueva primero. */
export function lastRunByScope(
  runs: RebalanceRunRow[] | undefined,
): Record<RebalanceScope, RebalanceRunRow | null> {
  const out: Record<RebalanceScope, RebalanceRunRow | null> = { full: null, closer: null };
  for (const r of runs ?? []) {
    if ((r.scope === 'full' || r.scope === 'closer') && out[r.scope] == null) out[r.scope] = r;
  }
  return out;
}

function nyDateKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: NY }).format(d);
}

/** «hoy 10:17 PM» si fue hoy en Nueva York; si no, la fecha corta y la hora. */
function nyWhen(iso: string, lang: 'en' | 'es', todayLabel: (time: string) => string): string {
  try {
    const d = new Date(iso);
    const time = new Intl.DateTimeFormat(lang, {
      hour: 'numeric', minute: '2-digit', timeZone: NY,
    }).format(d);
    if (nyDateKey(d) === nyDateKey(new Date())) return todayLabel(time);
    const date = new Intl.DateTimeFormat(lang, {
      month: 'short', day: 'numeric', timeZone: NY,
    }).format(d);
    return `${date} ${time}`;
  } catch {
    return iso;
  }
}

/** Cómo terminó una corrida, en palabras (nunca el mensaje de error crudo). */
function statusKey(r: RebalanceRunRow): StringKey {
  if (r.status === 'failed') {
    return r.reason === 'stale' ? 'e36.rb.status.stale' : 'e36.rb.status.failed';
  }
  if (r.reason === 'switch_off') return 'e36.rb.status.switchOff';
  if (r.reason === 'empty_group') return 'e36.rb.status.emptyGroup';
  return 'e36.rb.status.done';
}

export function NavRebalanceCard(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<RebalanceScope | null>(null);
  // El mensaje de la última respuesta del POST, por tipo: se queda a la vista
  // (el toast se va) para que «apagado en Config» no se pierda.
  const [notice, setNotice] = useState<Partial<Record<RebalanceScope, string>>>({});

  const runsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.rebalanceRuns,
    queryFn: () => adminNavApi.rebalanceRuns(),
    // Mientras haya una corrida viva, se re-mira sola cada pocos segundos.
    // Cuando ninguna corre, no se gasta nada.
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((r) => r.status === 'running') ? REBALANCE_POLL_MS : false,
  });
  const last = lastRunByScope(runsQ.data?.items);

  // Cuando una corrida termina, los conteos de abajo (buzón, cargas de cada
  // persona) quedaron viejos: se refrescan. Sin esto el admin vería «13 en el
  // buzón» después de un reparto que se los llevó.
  const runningBefore = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = new Set(
      (runsQ.data?.items ?? []).filter((r) => r.status === 'running').map((r) => r.id),
    );
    const termino = [...runningBefore.current].some((id) => !now.has(id));
    runningBefore.current = now;
    if (termino) {
      void qc.invalidateQueries({ queryKey: ['admin', 'navigation', 'bulk-reassign'] });
    }
  }, [runsQ.data, qc]);

  const typeLabel = (s: RebalanceScope): string =>
    t(s === 'full' ? 'e36.rb.type.full' : 'e36.rb.type.closer');

  const startMut = useMutation({
    mutationFn: (scope: RebalanceScope) => adminNavApi.rebalance(scope),
    onSuccess: (res: RebalanceStartResult, scope) => {
      const type = typeLabel(scope);
      let msg: string;
      if (res.started) {
        msg = t('e36.rb.started', { type });
        toast.success(msg);
      } else if (res.reason === 'switch_off') {
        msg = t('e36.rb.switchOff');
        toast.info(msg);
      } else if (res.reason === 'already_running') {
        msg = t('e36.rb.alreadyRunning', { type });
        toast.info(msg);
      } else {
        msg = t('e36.rb.notStarted', { reason: res.reason ?? '—' });
        toast.info(msg);
      }
      setNotice((n) => ({ ...n, [scope]: msg }));
      void qc.invalidateQueries({ queryKey: ADMIN_NAV_KEYS.rebalanceRuns });
    },
    onError: (e, scope) => {
      const msg = navError(e).message ?? '';
      toast.error(t('e36.rb.failed'), msg);
      setNotice((n) => ({ ...n, [scope]: `${t('e36.rb.failed')}${msg ? `: ${msg}` : ''}` }));
    },
  });

  const lastLine = (s: RebalanceScope): string => {
    const r = last[s];
    const type = typeLabel(s);
    if (!r) return t('e36.rb.never', { type });
    const when = nyWhen(r.startedAt, lang, (time) => t('e36.rb.today', { time }));
    if (r.status === 'running') return t('e36.rb.lastRunning', { type, when });
    const line = t('e36.rb.last', {
      when,
      type,
      n: new Intl.NumberFormat(lang).format(r.movedCases),
      status: t(statusKey(r)),
    });
    // Las automáticas (al crear o activar a alguien) también cuentan como
    // «última vez»; se marcan para que nadie crea que alguien apretó el botón.
    return r.trigger === 'manual' ? line : `${line} · ${t('e36.rb.auto')}`;
  };

  return (
    <section
      className="rounded-xl border-[1.5px] border-nav-card bg-white p-[22px]"
      aria-labelledby="e36-rebalance-kicker"
    >
      <p
        id="e36-rebalance-kicker"
        className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal mb-2"
      >
        {t('e36.rb.kicker')}
      </p>
      <p className="text-[13px] leading-relaxed text-nav-slate mb-4">{t('e36.rb.body')}</p>

      <div className="flex flex-wrap gap-2">
        {SCOPES.map((s) => {
          const running = last[s]?.status === 'running';
          const pending = startMut.isPending && startMut.variables === s;
          return (
            <button
              key={s}
              type="button"
              data-testid={`e36-rebalance-${s}`}
              disabled={running || pending}
              onClick={() => setConfirm(s)}
              className={cn(
                'inline-flex items-center gap-2 min-h-[44px] px-5 rounded-lg border-[1.5px] font-head text-[13.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                s === 'full'
                  ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover'
                  : 'bg-white text-nav-navy border-nav-navy hover:bg-nav-softer',
              )}
            >
              {(running || pending) && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {t(s === 'full' ? 'e36.rb.btn.full' : 'e36.rb.btn.closer')}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-col gap-1 text-[12.5px] text-nav-slate tabular-nums" aria-live="polite">
        {runsQ.isLoading ? (
          <p className="m-0 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          </p>
        ) : runsQ.isError ? (
          <p className="m-0 text-nav-red" role="alert">{t('e36.rb.runsErr')}</p>
        ) : (
          SCOPES.map((s) => (
            <p key={s} className="m-0" data-testid={`e36-rebalance-last-${s}`}>
              {lastLine(s)}
            </p>
          ))
        )}
        {SCOPES.map((s) => notice[s] && (
          <p
            key={`n-${s}`}
            className="m-0 text-nav-amberText"
            role="status"
            data-testid={`e36-rebalance-notice-${s}`}
          >
            {notice[s]}
          </p>
        ))}
      </div>

      <ConfirmDialog
        open={confirm != null}
        title={confirm ? t('e36.rb.confirmTitle', { type: typeLabel(confirm) }) : ''}
        message={t('e36.rb.body')}
        confirmLabel={confirm ? t('e36.rb.confirmGo', { type: typeLabel(confirm) }) : ''}
        cancelLabel={t('e36.rb.confirmNo')}
        tone="warning"
        loading={startMut.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          const s = confirm;
          setConfirm(null);
          if (s) startMut.mutate(s);
        }}
      />
    </section>
  );
}
